/**
 * Model store — leaf module (Perf 3.1.b Step 5). Cross-store refs via runtime getStores().
 */
import { create } from 'zustand';
import { modelsApi } from '@/lib/api/models';

const getStores = () => require('@/store');

const DEFAULT_OUTPUT_MODEL_KEY_BY_INTENT = {
  'create-image': 'imageModel',
  'edit-image': 'imageToImageModel',
  'create-video': 'videoModel',
  'image-to-video': 'imageToVideoModel',
  'create-vector': 'vectorModel',
  'vectorize-image': 'imageToVectorModel',
  'upscale-image': 'upscaleModel',
  'create-music': 'musicModel',
  'create-3d': 'threeDModel',
  'image-to-3d': 'imageTo3DModel',
  research: 'researchModel',
  coding: 'codingModel',
  'coding-quick': 'quickCodingModel',
  'coding-standard': 'codingModel',
  'coding-hard': 'hardCodingModel',
  'create-presentation': 'presentationModel',
  'create-pdf': 'documentModel',
  'create-spreadsheet': 'spreadsheetModel',
  'create-longform': 'longformModel',
  political: 'politicalModel',
};

// Models store
const useModelStore = create((set, get) => ({
  // Data states
  models: {}, // Map of model id to model object
  modelTags: {}, // Map of tag slug to tag object
  modelsByTag: {}, // Map of tag slug to array of model ids
  defaultOutputModels: null, // Intent-engine default output model slugs
  searchResults: [], // Array of model ids
  featuredModelIds: [], // Array of model ids
  frequentModelIds: [], // Array of user's most-used model ids
  allModelIds: [], // Array of all active model ids

  // Navigation/Current states
  currentModelTagSlug: null,
  searchQuery: '',
  
  // Loading states
  isLoadingModelTags: false,
  isLoadingModelsByTag: false,
  isLoadingModel: false,
  isLoadingDefaultOutputModels: false,
  isLoadingFeaturedModels: false,
  isLoadingFrequentModels: false,
  isLoadingAllModels: false,
  isSearching: false,
  
  // Fetch completion states
  isFetchedModelTags: false,
  isFetchedFeaturedModels: false,
  isFetchedFrequentModels: false,
  isFetchedAllModels: false,
  
  // Error states
  modelTagsError: null,
  modelsByTagError: null,
  modelError: null,
  defaultOutputModelsError: null,
  searchError: null,
  featuredModelsError: null,
  allModelsError: null,
  
  // Pagination states
  pagination: {
    modelTags: { current: 1, total: 1, perPage: 10 },
    modelsByTag: { current: 1, total: 1, perPage: 20 },
    search: { current: 1, total: 1, perPage: 20 },
    allModels: { current: 1, total: 0, perPage: 20, hasMore: false }
  },

  // Helper Functions
  addModels: (newModels) => {
    if (!Array.isArray(newModels)) {
      newModels = [newModels];
    }
    
    // Deduplicate models by ID using a Map
    const uniqueModelsMap = new Map();
    newModels.forEach(model => {
      if (model && model.id) {
        uniqueModelsMap.set(model.id, model);
      }
    });
    newModels = Array.from(uniqueModelsMap.values());
    
    // First, add any companies to the company store
    const companies = newModels.map(model => model.company).filter(Boolean);
    
    if (companies.length > 0) {
      getStores().useCompanyStore.getState().addCompanies(companies);
    }

    const processedModels = newModels.map(model => ({
      id: model.id,
      name: model.name,
      description: model.description,
      companyId: model?.company?.id || model.companyId || null,
      slug: model.slug,
      strengths: model.strengths,
      keywords: model.keywords,
      logoPath: model.logoPath,
      logoSizeBytes: model.logoSizeBytes,
      inputPlaceholder: model.inputPlaceholder || null,
      orderWeight: model.orderWeight,
      systemPrompt: model.systemPrompt,
      apiModelName: model.apiModelName,
      contextWindow: model.contextWindow,
      maxOutputTokens: model.maxOutputTokens,
      maxInputTokens: model.maxInputTokens,
      capabilities: model.capabilities,
      conversationStarters: model.conversationStarters,
      flags: {
        isEnabled: model.flags?.isEnabled,
        isOpenSource: model.flags?.isOpenSource,
        isPlusOnly: model.flags?.isPlusOnly,
        isFeatured: model.flags?.isFeatured,
        isDeprecated: model.flags?.isDeprecated,
        isApiKeyRequired: model.flags?.isApiKeyRequired,
      },
      creditCosts: {
        baseCost: model.creditCosts?.baseCost,
        contextThreshold: model.creditCosts?.contextThreshold,
        contextMultiplier: model.creditCosts?.contextMultiplier,
        isEnabled: model.creditCosts?.isEnabled,
      },
      tags: model.tags,
      stats: model.stats,
      parameters: model.parameters,
      trainingCutoffDate: model.trainingCutoffDate,
      releasedDate: model.releasedDate,
      archivedAt: model.archivedAt,
      createdAt: model.createdAt,
      updatedAt: model.updatedAt,
      deletedAt: model.deletedAt,
    }));
    
    set(state => ({
      models: {
        ...state.models,
        ...Object.fromEntries(processedModels.map(model => [model.id, model]))
      }
    }));

    return processedModels;
  },

  addModelTags: (newTags) => {
    if (!Array.isArray(newTags)) {
      newTags = [newTags];
    }
    
    const processedTags = newTags.map(tag => ({
      id: tag.id,
      name: tag.name,
      description: tag.description,
      slug: tag.slug,
      icon: tag.icon,
      flags: {
        isEnabled: tag.flags?.isEnabled ?? true
      },
      stats: tag.stats || {},
      createdAt: tag.createdAt,
      updatedAt: tag.updatedAt
    }));
    
    set(state => ({
      modelTags: {
        ...state.modelTags,
        ...Object.fromEntries(processedTags.map(tag => [tag.slug, tag]))
      }
    }));

    return processedTags;
  },

  // Fetch Functions
  fetchModelById: async (modelId) => {
    try {
      set({ isLoadingModel: true, modelError: null });
      
      // Check if we already have this model in the store
      const existingModel = get().models[modelId];
      if (existingModel) {
        set({ isLoadingModel: false });
        return existingModel;
      }

      const model = await modelsApi.getModelById(modelId);
      const processedModel = get().addModels(model)[0];
      
      set({ isLoadingModel: false });
      return processedModel;
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch model';
      set({ 
        modelError: errorMessage,
        isLoadingModel: false 
      });
      throw new Error(errorMessage);
    }
  },

  // Fetch model by slug from API
  fetchModelBySlug: async (slug) => {
    try {
      // First check if we already have this model in the store
      const existingModel = get().getModelBySlug(slug);
      if (existingModel) {
        return existingModel;
      }

      // Phase 8 of the public-dashboard rollout opened `/json/models/:slug`
      // to anonymous visitors (now uses `jwtAuthMiddlewareOptional`), so
      // we can fetch model details for both anon and authed callers.
      set({ isLoadingModel: true, modelError: null });

      const model = await modelsApi.getModelBySlug(slug);
      const processedModel = get().addModels(model)[0];

      set({ isLoadingModel: false });
      return processedModel;
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch model by slug';
      set({ 
        modelError: errorMessage,
        isLoadingModel: false 
      });
      console.warn(`fetchModelBySlug(${slug}):`, errorMessage);
      return null;
    }
  },

  fetchDefaultOutputModels: async ({ force = false } = {}) => {
    const existingDefaults = get().defaultOutputModels;
    if (existingDefaults && !force) {
      return existingDefaults;
    }

    try {
      set({ isLoadingDefaultOutputModels: true, defaultOutputModelsError: null });
      const defaults = await modelsApi.getDefaultOutputModels();
      set({
        defaultOutputModels: defaults || {},
        isLoadingDefaultOutputModels: false,
      });
      return defaults || {};
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch default output models';
      set({
        defaultOutputModelsError: errorMessage,
        isLoadingDefaultOutputModels: false,
      });
      console.warn('fetchDefaultOutputModels:', errorMessage);
      return existingDefaults || {};
    }
  },

  getDefaultOutputModelSlug: (intentType, fallbackSlug = null) => {
    const key = DEFAULT_OUTPUT_MODEL_KEY_BY_INTENT[intentType];
    return (key && get().defaultOutputModels?.[key]) || fallbackSlug;
  },

  resolveDefaultOutputModel: async (intentType, fallbackSlug = null, { force = false } = {}) => {
    const defaults = force
      ? await get().fetchDefaultOutputModels({ force: true })
      : (get().defaultOutputModels || await get().fetchDefaultOutputModels());
    const key = DEFAULT_OUTPUT_MODEL_KEY_BY_INTENT[intentType];
    const configuredSlug = key ? defaults?.[key] : null;
    const slug = configuredSlug || fallbackSlug;
    if (!slug) return null;

    let model = get().getModelBySlug(slug) || await get().fetchModelBySlug(slug);

    if (!model && fallbackSlug && fallbackSlug !== slug) {
      model = get().getModelBySlug(fallbackSlug) || await get().fetchModelBySlug(fallbackSlug);
    }

    return model;
  },

  fetchModelsByTag: async (slug, page = 1) => {
    try {
      set({ 
        isLoadingModelsByTag: true, 
        modelsByTagError: null 
      });

      const { modelTag, models, pagination } = await modelsApi.getModelsByTag(slug, page);
      
      if (modelTag) {
        get().addModelTags(modelTag);
      }

      const processedModels = get().addModels(models);
      
      set(state => ({
        modelsByTag: {
          ...state.modelsByTag,
          [slug]: page === 1 
            ? processedModels.map(model => model.id)
            : [...(state.modelsByTag[slug] || []), ...processedModels.map(model => model.id)]
        },
        pagination: {
          ...state.pagination,
          modelsByTag: {
            current: pagination.current,
            total: pagination.pages,
            perPage: pagination.perPage
          }
        },
        currentModelTagSlug: slug,
        isLoadingModelsByTag: false
      }));

      return processedModels;
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch models for tag';
      // Set empty array for the tag to prevent repeated fetch attempts
      set(state => ({ 
        modelsByTagError: errorMessage,
        isLoadingModelsByTag: false,
        modelsByTag: {
          ...state.modelsByTag,
          [slug]: state.modelsByTag[slug] || []
        }
      }));
      // Don't throw - let components handle the error state gracefully
      console.warn(`fetchModelsByTag(${slug}):`, errorMessage);
      return [];
    }
  },

  fetchFeaturedModels: async () => {
    // Skip if already fetched or currently loading
    if (get().isFetchedFeaturedModels || get().isLoadingFeaturedModels) {
      return;
    }

    try {
      set({ isLoadingFeaturedModels: true, featuredModelsError: null });
      const models = await modelsApi.getFeaturedModels();
      
      const processedModels = get().addModels(models);
      
      set({ 
        featuredModelIds: processedModels.map(model => model.id),
        isLoadingFeaturedModels: false,
        isFetchedFeaturedModels: true
      });

      return processedModels;
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch featured models';
      set({ 
        featuredModelsError: errorMessage,
        isLoadingFeaturedModels: false,
        isFetchedFeaturedModels: true
      });
      // Don't throw - error is stored in state for UI to display
      console.error('Failed to fetch featured models:', error);
      return null;
    }
  },

  fetchFrequentModels: async () => {
    if (get().isFetchedFrequentModels || get().isLoadingFrequentModels) {
      return;
    }

    try {
      set({ isLoadingFrequentModels: true });
      const models = await modelsApi.getFrequentModels();

      const processedModels = get().addModels(models);

      set({
        frequentModelIds: processedModels.map(model => model.id),
        isLoadingFrequentModels: false,
        isFetchedFrequentModels: true
      });

      return processedModels;
    } catch (error) {
      set({
        isLoadingFrequentModels: false,
        isFetchedFrequentModels: true
      });
      console.error('Failed to fetch frequent models:', error);
      return null;
    }
  },

  // `autoLoadAll` (default true) controls whether the action chains itself
  // through every remaining page after the first response lands. The
  // catalog page passes `false` so it can drive pagination from a scroll
  // handler instead of slamming the API with N requests on mount.
  fetchAllModels: async (page = 1, { autoLoadAll = true } = {}) => {
    if (get().isLoadingAllModels) {
      return;
    }

    if (page === 1 && get().isFetchedAllModels) {
      return;
    }

    try {
      set({ isLoadingAllModels: true, allModelsError: null });
      const { models, pagination } = await modelsApi.getAllModels(page);
      
      const processedModels = get().addModels(models);
      const newModelIds = processedModels.map(model => model.id);
      
      set(state => ({ 
        allModelIds: page === 1 ? newModelIds : [...new Set([...state.allModelIds, ...newModelIds])],
        isLoadingAllModels: false,
        isFetchedAllModels: !pagination.hasMore,
        pagination: {
          ...state.pagination,
          allModels: pagination
        }
      }));

      if (autoLoadAll && pagination.hasMore) {
        const nextPage = (pagination.current || page) + 1;
        setTimeout(() => get().fetchAllModels(nextPage), 50);
      }

      return processedModels;
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch all models';
      set({ 
        allModelsError: errorMessage,
        isLoadingAllModels: false,
        isFetchedAllModels: true
      });
      console.error('Failed to fetch all models:', error);
      return null;
    }
  },

  // Fetch all model tags
  fetchModelTags: async () => {
    // Skip if already fetched or currently loading
    if (get().isFetchedModelTags || get().isLoadingModelTags) {
      return;
    }

    try {
      set({ isLoadingModelTags: true, modelTagsError: null });
      const tags = await modelsApi.getAllModelTags();
      
      // Process tags through addModelTags helper
      get().addModelTags(tags);
      
      set({ isLoadingModelTags: false, isFetchedModelTags: true });
      return tags;
    } catch (error) {
      set({ 
        modelTagsError: error.message || 'Failed to fetch model tags',
        isLoadingModelTags: false,
        isFetchedModelTags: true
      });
      throw error;
    }
  },
  
  // Search Functions
  searchModels: async (query, page = 1) => {
    if (!query.trim()) {
      set({ 
        searchResults: [],
        searchQuery: '',
        pagination: {
          ...get().pagination,
          search: { current: 1, total: 1, perPage: 20 }
        },
        searchError: null
      });
      return;
    }

    try {
      set({ isSearching: true, searchError: null });
      const { models, pagination } = await modelsApi.search(query, page);
      
      const processedModels = get().addModels(models);
      
      set(state => ({ 
        searchQuery: query,
        searchResults: page === 1 ? processedModels : [...state.searchResults, ...processedModels],
        pagination: {
          ...state.pagination,
          search: {
            current: pagination.current,
            total: pagination.pages,
            perPage: pagination.perPage
          }
        }
      }));
      
      return processedModels;
    } catch (error) {
      const errorMessage = error.message || 'Failed to search models';
      set({ 
        searchError: errorMessage,
        isSearching: false 
      });
      throw new Error(errorMessage);
    } finally {
      set({ isSearching: false });
    }
  },

  clearSearch: () => {
    set(state => ({
      searchQuery: '',
      searchResults: [],
      pagination: {
        ...state.pagination,
        search: { current: 1, total: 1, perPage: 20 }
      },
      searchError: null
    }));
  },

  // Getter Functions
  getModelById: (modelId) => {
    return get().models[modelId]
  },

  getModelBySlug: (slug) => {
    const models = get().models;
    return Object.values(models).find(model => model.slug === slug);
  },

  getDeepModelById: (modelId) => {
    if (modelId == null || modelId === '') return null;
    const models = get().models;
    let model = models[modelId];
    if (!model && typeof modelId === 'object') {
      const nested = modelId.id ?? modelId._id;
      if (nested != null) model = models[nested] || models[String(nested)];
    }
    if (!model) {
      const key = typeof modelId === 'string' ? modelId : String(modelId?.id || modelId?._id || modelId || '');
      if (key && key !== '[object Object]') model = models[key];
    }
    if (!model) return null;
    
    // Get company information if companyId exists
    if (model.companyId) {
      const company = getStores().useCompanyStore.getState().getCompanyById(model.companyId);
      return {
        ...model,
        company
      };
    }
    
    return model;
  },

  getModelTagBySlug: (slug) => {
    return get().modelTags[slug]
  },
  getAllModelTags: () => {
    return Object.values(get().modelTags)
  },
  getModelsByTag: (slug) => {
    const modelIds = get().modelsByTag[slug] || [];
    return modelIds.map(id => get().models[id]).filter(Boolean);
  },

  // Clear/Reset Functions
  clearModelTags: () => {
    set(state => ({
      modelTags: {},
      currentModelTagSlug: null,
      pagination: {
        ...state.pagination,
        modelTags: { current: 1, total: 1, perPage: 10 }
      }
    }));
  },

  clearModelsByTag: () => {
    set(state => ({
      modelsByTag: {},
      isLoadingModelsByTag: false,
      modelsByTagError: null,
      pagination: {
        ...state.pagination,
        modelsByTag: { current: 1, total: 1, perPage: 20 }
      }
    }));
  },

  clearModels: () => {
    set({
      // Reset all states to initial values
      models: {},
      modelTags: {},
      modelsByTag: {},
      searchResults: [],
      featuredModelIds: [],
      frequentModelIds: [],
      allModelIds: [],
      currentModelTagSlug: null,
      searchQuery: '',
      isLoadingModelsByTag: false,
      isLoadingModel: false,
      isLoadingFeaturedModels: false,
      isLoadingFrequentModels: false,
      isLoadingAllModels: false,
      isLoadingModelTags: false, 
      isSearching: false,
      isFetchedModelTags: false,
      isFetchedFeaturedModels: false,
      isFetchedFrequentModels: false,
      isFetchedAllModels: false,
      modelsByTagError: null,
      modelError: null,
      searchError: null,
      featuredModelsError: null,
      allModelsError: null,
      modelTagsError: null,
      pagination: {
        modelTags: { current: 1, total: 1, perPage: 10 },
        modelsByTag: { current: 1, total: 1, perPage: 20 },
        search: { current: 1, total: 1, perPage: 20 },
        allModels: { current: 1, total: 0, perPage: 20, hasMore: false }
      }
    });
  }
}));

export { useModelStore };
export default useModelStore;
